import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useEffect } from "react";
import PortalAccessGate from "src/components/app/PortalAccessGate/PortalAccessGate";
import PortalGuidance from "src/components/app/PortalGuidance/PortalGuidance";
import PortalSidebar from "src/components/app/PortalSidebar/PortalSidebar";
import AppLayout from "src/components/layout/AppLayout/AppLayout";
import { lazyComponent, preloadLazyModule } from "src/lib/LazyModules";
import { metadataHead } from "src/lib/Metadata";
import { getModerationPolicyChoiceProject } from "src/lib/ModerationPolicyChoice";
import { portalScopeKey, portalScopeOf } from "src/lib/PortalPaths";
import { getLoadedPortalScopeKey, setLoadedPortalScopeKey } from "src/lib/ScopedLoaders";
import { getRequiredTermsStatus } from "src/lib/TermsAcceptance";
import { InlineRouteErrorPage } from "src/routes/-components/RouteBoundaries";
import { getPortalLayoutData } from "src/server/functions/AuthState";

// The portal is indexable so it can be found in search, but every page stays behind sign-in: signed-out
// visitors (and crawlers) get the sign-in screen, so all portal URLs point search engines at /portal.
export const Route = createFileRoute("/_authed/portal")({
  // Session, projects, the selected project's team and the moderation profiles, loaded with the
  // document and kept until `router.invalidate()`.
  // Signed-in visitors' shell widgets (account menu, notifications, project menu) load with it.
  // So is the blocking Terms dialog, for people who haven't accepted the current Terms, and then the
  // one-time policy choice for the project created automatically at first sign-in.
  // The data is for the URL's scope (`/portal/<org>/<project>/…`): it loads again when a page in
  // another scope opens. Account pages keep the scope they were opened from.
  loader: async ({ location, preload }) => {
    const scope = portalScopeOf(location.pathname);
    // A preload (pointing at a link) doesn't put its data on screen.
    if (typeof window !== "undefined" && !preload) setLoadedPortalScopeKey(portalScopeKey(scope));
    const data = await getPortalLayoutData({ data: scope });
    await Promise.all([
      data.authState?.user ? preloadLazyModule("signedInShell") : undefined,
      getRequiredTermsStatus(data.authState)
        ? preloadLazyModule("termsAcceptanceDialog")
        : undefined,
      getModerationPolicyChoiceProject(data)
        ? preloadLazyModule("moderationPolicyChoiceDialog")
        : undefined,
    ]);
    return { ...data, scopeKey: portalScopeKey(scope) };
  },
  shouldReload: ({ location }) => {
    const key = portalScopeKey(portalScopeOf(location.pathname));
    return key !== "" && key !== getLoadedPortalScopeKey();
  },
  staleTime: Number.POSITIVE_INFINITY,
  head: () =>
    metadataHead({
      title: "Portal",
      description:
        "Sign in to the CalmLens portal to review moderated assets, manage projects, API keys, webhooks, connectors and usage.",
      alternates: { canonical: "/portal" },
    }),
  component: PortalLayout,
  errorComponent: InlineRouteErrorPage,
});

const TermsAcceptanceDialog = lazyComponent("termsAcceptanceDialog", "default");
const ModerationPolicyChoiceDialog = lazyComponent("moderationPolicyChoiceDialog", "default");

function PortalLayout() {
  const {
    authState,
    organisations,
    selectedOrgId,
    projects,
    selectedProjectId,
    projectTeam,
    moderationProfiles,
    shellData,
    platformState,
    scopeKey,
  } = Route.useLoaderData();
  useEffect(() => {
    setLoadedPortalScopeKey(scopeKey);
  }, [scopeKey]);
  // Only the portal asks: the rest of the site (docs, blog, policies) stays readable.
  const requiredTermsStatus = getRequiredTermsStatus(authState);
  const policyChoiceProject = getModerationPolicyChoiceProject({
    authState,
    projects,
    selectedProjectId,
    moderationProfiles,
  });
  return (
    <>
      <AppLayout
        initialProjects={projects}
        initialOrganisations={organisations}
        initialSelectedOrgId={selectedOrgId}
        initialAuthState={authState}
        initialSelectedProjectId={selectedProjectId}
        initialProjectTeam={projectTeam}
        initialModerationProfiles={moderationProfiles}
        initialShellData={shellData}
        sidebar={<PortalSidebar platformState={platformState} />}
        sidebarTheme="dark"
      >
        {/* Inside the portal context: choosing assigns the policy as the policies page does. */}
        {policyChoiceProject ? (
          <ModerationPolicyChoiceDialog
            key={policyChoiceProject.id}
            project={policyChoiceProject}
          />
        ) : null}
        {/* Sends people away from pages their project role can't use, e.g. after a project switch. */}
        <PortalAccessGate>
          <Outlet />
          <PortalGuidance />
        </PortalAccessGate>
      </AppLayout>
      {requiredTermsStatus ? <TermsAcceptanceDialog termsStatus={requiredTermsStatus} /> : null}
    </>
  );
}
